import React from 'react';

import CustomizedTable from 'components/common/components/tablev2/CustomizedTable';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { setSelectedZones } from 'store/actions/dashboard/selectedZones';
import { setDashboardSelectedUavos } from 'store/actions/dashboard/selectedUavos';
import { setSelectedApprovedAreas } from 'store/actions/map/selectedApprovedAreas';
import { hasObjectKeys, parseDate } from 'utils/main';
import { setDashboardSelectedOperationsApprovals } from 'store/actions/dashboard/selectedOperationsApprovals';
import { setDashboardItemSelected } from 'store/actions/dashboard/setDashboardItemSelected';
import { authPatchRequestWithParam } from 'helpers/apiRequests';
import { clearMapLayer, createLayer } from 'utils/map/layers';
import { addLayerToMap, zoomToGeojsonGeometryWithBuffer } from 'utils/map/map';
import useStyles from 'consts/styles/RightScreenPart';
import { userStyles } from 'consts/styles/User';
import Icons from 'consts/icons';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import DisplayOperationApprovalDetails from './DisplayDetails';
import { addOperationsToLayer } from './utils/utils';

const DashboardOperationsApprovals = props => {
  const { isDashboardItemSelected, currentOperationsApprovals } = props;

  const timezone = useSelector(state => state.timezone);
  const map = useSelector(state => state.map);
  const subtitles = useSelector(state => state.language).close_;

  const [selectedOperation, setSelectedOperation] = React.useState(null);
  const [confirmationModalCase, setConfirmationModalCase] = React.useState(
    null
  );
  const layer = React.useRef(null);

  const classes = useStyles();
  const classesUser = userStyles();

  const dispatch = useDispatch();

  React.useEffect(() => {
    layer.current = createLayer('operationsApprovals');
  }, []);

  const initMap = () => {
    if (!hasObjectKeys(map)) return;
    addLayerToMap(map, layer.current);
  };

  React.useEffect(initMap, [map]);

  React.useEffect(() => {
    if (selectedOperation) {
      dispatch(setDashboardItemSelected(true));
      clearMapLayer(layer.current);
      addOperationsToLayer(layer.current, [selectedOperation]);
      handleZoomAfterSelect();
    } else {
      dispatch(setDashboardItemSelected(false));
      clearMapLayer(layer.current);
      setConfirmationModalCase(null);
    }
  }, [selectedOperation]);

  const rowClicked = data => {
    setSelectedOperation(
      currentOperationsApprovals.find(operation => operation.uid === data.id)
    );
    dispatch(setDashboardItemSelected(true));
    dispatch(setSelectedZones([]));
    dispatch(setDashboardSelectedUavos([]));
    dispatch(setSelectedApprovedAreas([]));
  };

  const updateApproval = async (approve = null, message = null) => {
    if (!approve || confirmationModalCase === null) {
      setConfirmationModalCase(null);
      return;
    }
    const response = await authPatchRequestWithParam(
      'operationApproval',
      selectedOperation.uid,
      {
        status: confirmationModalCase ? 'accepted' : 'rejected',
        message,
      }
    );
    if (response?.status === 200) {
      const filteredOperations = currentOperationsApprovals.filter(
        approval => approval.uid !== response.data.uid
      );
      dispatch(setDashboardSelectedOperationsApprovals(filteredOperations));
      setSelectedOperation(null);
      clearMapLayer(layer.current);
    }
  };

  const handleZoomAfterSelect = () => {
    zoomToGeojsonGeometryWithBuffer(
      map,
      selectedOperation.operation.geojson,
      200000
    );
  };

  const handleZoom = () => {
    zoomToGeojsonGeometryWithBuffer(map, selectedOperation.operation.geojson);
  };

  React.useEffect(() => {
    if (!isDashboardItemSelected) setSelectedOperation(null);
  }, [isDashboardItemSelected]);

  return (
    <>
      <div className={`${classes.Wrapper}__switcher-wrapper`}>
        <CustomizedTable
          headers={['Uid', 'Start', 'Stop']}
          data={currentOperationsApprovals.map(approval => ({
            id: approval.uid,
            start: parseDate(approval.operation.start, timezone),
            stop: parseDate(approval.operation.stop, timezone),
          }))}
          onClickFunction={rowClicked}
          selectedRow={selectedOperation}
          dashboard
        />
      </div>
      {selectedOperation ? (
        <Grid
          container
          className={`${classesUser.User}__dashboard ${classesUser.User}`}
        >
          <Tooltip title={subtitles} placement="bottom">
            <div
              className={`${classesUser.User}__settings__close`}
              onClick={() => setSelectedOperation(null)}
              onKeyPress={() => setSelectedOperation(null)}
              role="button"
              tabIndex={0}
            >
              <div
                className={`${classesUser.User}__settings__close__container`}
              >
                <Icons.Actions.Close />
              </div>
            </div>
          </Tooltip>
          <DisplayOperationApprovalDetails
            selectedOperation={selectedOperation}
            setSelectedOperation={setSelectedOperation}
            confirmationModalCase={confirmationModalCase}
            setConfirmationModalCase={setConfirmationModalCase}
            handleZoom={handleZoom}
            confirmAction={updateApproval}
          />
        </Grid>
      ) : null}
    </>
  );
};

DashboardOperationsApprovals.propTypes = {
  isDashboardItemSelected: PropTypes.bool,
  currentOperationsApprovals: PropTypes.array.isRequired,
};

const mapStateToProps = state => ({
  isDashboardItemSelected: state.isDashboardItemSelected,
});

export default connect(mapStateToProps)(
  React.memo(DashboardOperationsApprovals)
);
